import React from 'react';
import { Controller } from 'react-hook-form';
import { Autocomplete, TextField } from '@mui/material';
import { get } from 'lodash';
import {
  CREDIT_NOTE_DOCUMENT_TYPE_CODE,
  GOODS_AND_SERVICE_CN_CODE,
  INVOICE_DOCUMENT_TYPE_CODE,
  OUT_OF_SCOPE_INVOICE_CODE,
  SELF_BILLED_CREDIT_NOTE_DOCUMENT_TYPE_CODE,
  SELF_BILLED_INVOICE_DOCUMENT_TYPE_CODE,
} from '../../constants/defaults';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';

const DocumentTypeSelect = ({
  isPurchase,
  documentType,
  id,
  name,
  label,
  control,
  errors,
  readOnly = false,
  onChange,
  helperText,
  disabled,
}) => {
  const { common } = useTranslatedMessages();
  const t = common?.document_type_select ?? {};
  const resolvedLabel = label ?? t?.label;
  const DOCUMENT_TYPE_OPTIONS = {
    invoice: {
      salesOptions: [
        {
          value: INVOICE_DOCUMENT_TYPE_CODE,
          label: t?.commercial_invoice_label ?? 'Commercial invoice (Tax Invoice)',
        },
        {
          value: OUT_OF_SCOPE_INVOICE_CODE,
          label: t?.invoice_out_of_scope_label ?? 'Invoice out of scope of tax (Non-tax Invoice)',
        },
      ],
      purchaseOptions: [
        {
          value: SELF_BILLED_INVOICE_DOCUMENT_TYPE_CODE,
          label: t?.self_billed_invoice_label ?? 'Self-billed invoice',
        },
      ],
    },
    creditNote: {
      salesOptions: [
        {
          value: CREDIT_NOTE_DOCUMENT_TYPE_CODE,
          label: t?.credit_note_label ?? 'Commercial credit note (Tax Credit Note)',
        },
        {
          value: GOODS_AND_SERVICE_CN_CODE,
          label: t?.credit_note_goods_services_label ?? 'Credit note out of scope of tax (Non-tax Credit Note)',
        },
      ],
      purchaseOptions: [
        {
          value: SELF_BILLED_CREDIT_NOTE_DOCUMENT_TYPE_CODE,
          label: t?.self_billed_credit_note_label ?? 'Self billed credit note',
        },
      ],
    },
  };
  const { salesOptions = [], purchaseOptions = [] } = DOCUMENT_TYPE_OPTIONS[documentType] || {};
  const sales = salesOptions;
  const purchase = purchaseOptions;
  const options = readOnly ? [...sales, ...purchase] : isPurchase ? purchase : sales;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const selectedOption = options.find((opt) => opt.value === field.value) || null;
        const errorMessage = get(errors, name, {})?.message;
        const combinedHelperText = [helperText, errorMessage].filter(Boolean).join(' * ');

        return (
          <Autocomplete
            disabled={disabled}
            id={id || name}
            options={options}
            getOptionLabel={(option) => `${option?.value} - ${option?.label}` || ''}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            value={selectedOption}
            onChange={(_, selected) => {
              if (selected) {
                field.onChange(selected.value);
                if (onChange) {
                  onChange({ value: selected.value });
                }
              } else {
                field.onChange(null);
                if (onChange) {
                  onChange({ value: null });
                }
              }
            }}
            readOnly={readOnly}
            renderInput={(params) => (
              <TextField {...params} label={resolvedLabel} error={!!errorMessage} helperText={combinedHelperText} />
            )}
          />
        );
      }}
    />
  );
};

export default DocumentTypeSelect;
